import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Alert, Typography, Flex } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { useNavigate, useLocation } from 'react-router-dom';
import { auth } from '../../firebase';
import useAuthStore from '../../stores/authStore';
import useTenantStore from '../../stores/tenantStore';
import { APP_NAME } from '../../constants/appName';

const { Title, Text } = Typography;

interface LoginFormValues {
  email: string;
  password: string;
}

export default function LoginPage(): React.ReactElement {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const tenant = useTenantStore((s) => s.tenant);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const navigate = useNavigate();
  const location = useLocation();

  // Redirect once auth + backend verify are complete
  useEffect(() => {
    if (isAuthenticated) {
      const from = (location.state as { from?: { pathname: string } })?.from?.pathname ?? '/';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location]);

  const handleSubmit = async (values: LoginFormValues) => {
    setLoading(true);
    setError(null);
    try {
      await signInWithEmailAndPassword(auth, values.email, values.password);
      // onAuthStateChanged in useAuthBootstrap handles the rest
    } catch (err: unknown) {
      const code = (err as { code?: string }).code;
      if (
        code === 'auth/user-not-found' ||
        code === 'auth/wrong-password' ||
        code === 'auth/invalid-credential'
      ) {
        setError('Courriel ou mot de passe incorrect.');
      } else if (code === 'auth/too-many-requests') {
        setError('Trop de tentatives. Veuillez réessayer dans quelques minutes.');
      } else {
        setError("Une erreur est survenue. Veuillez réessayer.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Flex
      justify="center"
      align="center"
      style={{ minHeight: '100vh', background: '#f0f2f5' }}
    >
      <div
        style={{
          width: 400,
          background: '#fff',
          borderRadius: 12,
          padding: '48px 40px',
          boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
        }}
      >
        {/* Logo / Branding */}
        <Flex justify="center" style={{ marginBottom: 32 }}>
          {tenant.logo_url ? (
            <img
              src={tenant.logo_url}
              alt={tenant.company_name}
              style={{ maxHeight: 64, maxWidth: 240, objectFit: 'contain' }}
            />
          ) : (
            <Title level={3} style={{ margin: 0, color: tenant.primary_color }}>
              {APP_NAME}
            </Title>
          )}
        </Flex>

        <Title level={4} style={{ marginBottom: 8, fontWeight: 500 }}>
          Connexion
        </Title>
        <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
          Accès réservé aux membres de l'équipe.
        </Text>

        {error && (
          <Alert
            message={error}
            type="error"
            showIcon
            style={{ marginBottom: 20 }}
          />
        )}

        <Form<LoginFormValues>
          layout="vertical"
          onFinish={handleSubmit}
          size="large"
          requiredMark={false}
        >
          <Form.Item
            name="email"
            label="Courriel"
            rules={[
              { required: true, message: 'Le courriel est requis.' },
              { type: 'email', message: 'Format de courriel invalide.' },
            ]}
          >
            <Input
              prefix={<UserOutlined />}
              placeholder="nom@organisation.qc.ca"
              autoComplete="email"
            />
          </Form.Item>

          <Form.Item
            name="password"
            label="Mot de passe"
            rules={[{ required: true, message: 'Le mot de passe est requis.' }]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="••••••••"
              autoComplete="current-password"
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Button
              type="primary"
              htmlType="submit"
              block
              loading={loading}
              style={{ background: tenant.primary_color }}
            >
              Se connecter
            </Button>
          </Form.Item>
        </Form>
      </div>
    </Flex>
  );
}
